import React, { useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';

import {
    AdvertisementText,
    Button,
    Glyphicon,
    Image,
    Link,
    PageSection,
    SubHeading,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { ContentPreview } from 'reaxl-molecules';

import contentAnalyticsHandlers from '@/analytics/contentAnalyticsHandlers';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

const renderSponsoredText = (sponsoredText) => sponsoredText && (
    <AdvertisementText
        color="subdued"
        componentClass="div"
    >
        {sponsoredText}
    </AdvertisementText>
);

const renderTitle = (title) => title && (
    <SubHeading
        level={2}
    >
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: title }} />
    </SubHeading>
);

const renderLink = (link, sponsoredText, cmp, onResourcesTextLinkClick = () => { }) => (
    // Renders a button that looks like a link for tagging purposes.
    // Entire section acts as the interactive link
    (
        <Button
            bsStyle="text"
            onClick={onResourcesTextLinkClick}
        >
            {cmp === 'ny_syc' && (
                <img
                // eslint-disable-next-line no-template-curly-in-string
                    src="https://ad.doubleclick.net/ddm/trackimp/N3973.1360.KELLEYBLUEBOOK/B27554392.333476565;dc_trk_aid=525411378;dc_trk_cid=100510000;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=?"
                    border="0"
                    height="1"
                    width="1"
                    alt="Advertisement"
                />
            )}
            {/* eslint-disable-next-line react/no-danger */}
            <span dangerouslySetInnerHTML={{ __html: link.label }} />
            {(!!sponsoredText || link.label === 'Finance with us') && (
                <>
                    {' '}
                    <Glyphicon glyph="offsite" />
                </>
            )}
        </Button>
    )
);

const renderImage = (image) => image && (
    <Image
        className="margin-right-sm-3"
        src={image.src}
        alt={image.alt}
        width="68"
        height="68"
    />
);

export const getContentPreview = (
    resources,
    hideAds,
    handleResourcesClick,
    handleResourcesImpression,
    handleResourcesTextLinkClick,
    theme,
) => resources.filter((details) => !hideAds || !details.sponsoredText).map((details = {}) => {

    const { title, image, link, cmp, epn, sponsoredText } = details;
    const data = {
        cmp,
        epn,
    };

    const linkCtas = { ...link, rel: 'nofollow noopener' };
    if (link?.href?.includes('instant-cash-offer')) {
        linkCtas.href = link.href.includes('?') ? `${link.href}&LNX=TIMATCVDPCALCTXT` : `${link.href}?LNX=TIMATCVDPCALCTXT`;
        linkCtas.rel = 'noopener';
    }

    const onResourcesTextLinkClick = handleResourcesTextLinkClick(details);

    return (
        <DelayedImpressionObserver
            onChange={handleResourcesImpression(details)}
            key={`${cmp}-resources-delayed-impression`}
            className={clsx('col-xs-12 col-sm-5', {
                'margin-bottom-4': !theme,
            })}
            data-qaid={cmp}
        >
            <Link
                href={linkCtas.href}
                rel={linkCtas.rel}
                target="_blank"
                className="link-unstyled padding-bottom-2 padding-top-2"
                onClick={handleResourcesClick(data)}
                undecorated
            >
                <ContentPreview
                    image={renderImage(image)}
                    category={renderSponsoredText(sponsoredText)}
                    headline={renderTitle(title)}
                    summary={renderLink(linkCtas, sponsoredText, cmp, onResourcesTextLinkClick)}
                    orientation="landscape"
                />
            </Link>
        </DelayedImpressionObserver>
    );
});

function AdditionalResourcesContainer() {
    const activeInventory = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};

    const {
        resources: {
            additionalResources = [],
        } = {},
        finance = [],
        dealerConfig: {
            hideAds,
        } = {},
    } = activeInventory;

    const {
        additional_resources: [, {
            carchex: isCarchexEnabled,
            theme,
            bottomStyle,
        }],
    } = useFeatures([
        'additional_resources',
    ]);

    const resources = useMemo(() => [...finance, ...additionalResources], [finance, additionalResources]);

    const handleResourcesImpression = useCallback(
        ({ cmp, title, link }) => () => {
            const analyticsHandlerKey = {
                get_loan_txt: isCarchexEnabled ? ['featuredPartnerImpression', 'carchex'] : ['featuredPartnerImpression', 'capitalone'],
                apply_crd_vw_wb_btn: ['featuredPartnerImpression', 'financeWithUs'],
                get_quote_txt: ['featuredPartnerImpression', 'rootInsurance'],
                ny_syc: ['featuredPartnerImpression', 'experian'],
            };
            const impressionHandler = analyticsHandlerKey[cmp];
            if (impressionHandler) {
                const data = {
                    name: impressionHandler[1],
                    subhead: link?.label,
                    title,
                };

                if (cmp !== 'ny_syc') {
                    data.heading = `additional-resources::${title}`;
                }

                sendImpressions({
                    name: impressionHandler[0],
                    data,
                });
            }
        },
        [isCarchexEnabled]
    );

    const handleResourcesClick = useCallback(
        (data = {}) => (event) => {
            const analyticsHandlerKey = {
                apply_crd_vw_wb_btn: contentAnalyticsHandlers.financeWithUsLink,
                ns_tim: contentAnalyticsHandlers.kbbInstantCashClick,
                ny_syc: contentAnalyticsHandlers.creditReportClick,
                get_loan_txt: isCarchexEnabled
                    ? contentAnalyticsHandlers.warrantyResourceClick
                    : contentAnalyticsHandlers.capOneResourceClick,
                get_quote_txt: contentAnalyticsHandlers.rootInsuranceClick,
            };
            const clickHandler = analyticsHandlerKey[data.cmp];
            if (clickHandler) {
                event.stopPropagation();
                sendClick(clickHandler, event, data);
            }
        },
        [isCarchexEnabled]
    );

    const handleResourcesTextLinkClick = useCallback(
        (data = {}) => (event) => {
            if (data.cmp === 'get_quote_txt') {
                event.stopPropagation();
                sendClick(contentAnalyticsHandlers.rootInsuranceTextLinkClick, event, data);
            }
        },
        []
    );

    const contentPreviews = useMemo(
        () => getContentPreview(
            resources,
            hideAds,
            handleResourcesClick,
            handleResourcesImpression,
            handleResourcesTextLinkClick,
            theme,
        ),
        [resources, hideAds, handleResourcesClick, handleResourcesImpression, handleResourcesTextLinkClick, theme]
    );

    if (!resources.length) return null;

    const contentWrapper = (
        <div className={clsx(
            'display-flex flex-wrap text-left justify-content-between',
            { 'gap-5': !!theme }
        )}
        >
            {contentPreviews}
        </div>
    );

    return (
        <PageSection
            className="container-fluid"
            data-qaid="cntr-resources"
            uiContext="level-3"
            heading="More to explore on your car-buying journey"
            headingClassName="margin-bottom-5"
            alignment="left"
            theme={theme}
            bottomStyle={bottomStyle}
        >
            {(theme || bottomStyle) ? (
                <div className="row">
                    {contentWrapper}
                </div>
            ) : contentWrapper}
        </PageSection>
    );
}

export default AdditionalResourcesContainer;
